import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { AiTableArtifact } from '../ai-chat/aiChatTypes';
import { selectActiveChatId } from '../../aiAgentCenter/agentsChatsReducer';
import { useAgentChatInstanceOptional } from '../../aiAgentCenter/AgentChatInstanceContext';
import { ChatInstance } from '../../aiAgentCenter/ChatInstance';

interface TableArtifactContextType {
  // Panel state
  isTablePanelOpen: boolean;
  currentTableId: string | null;
  isLoading: boolean;
  error: Error | null;

  // Data - cached table artifacts
  tableArtifacts: Record<string, AiTableArtifact>;

  // Actions
  openTablePanel: (tableId: string) => void;
  closeTablePanel: () => void;
  setTableData: (tableId: string, data: AiTableArtifact) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: Error | null) => void;
}

const TableArtifactContext = createContext<TableArtifactContextType | null>(null);

export const TableArtifactProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isTablePanelOpen, setIsTablePanelOpen] = useState(false);
  const [currentTableId, setCurrentTableId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [tableArtifacts, setTableArtifacts] = useState<Record<string, AiTableArtifact>>({});

  // Get current active chat ID to detect changes
  const chatInstanceContext = useAgentChatInstanceOptional();
  const chatInstanceId = chatInstanceContext?.chatInstanceId ?? ChatInstance.agentsPage;
  const activeChatId = useSelector(selectActiveChatId(chatInstanceId));
  const previousActiveChatId = useRef<string | undefined>(activeChatId);

  const resetTableState = useCallback(() => {
    setIsTablePanelOpen(false);
    setCurrentTableId(null);
    setError(null);
  }, []);

  // Close table panel when active chat changes
  useEffect(() => {
    const chatIdChanged = previousActiveChatId.current !== activeChatId;
    const hadPreviousChat = previousActiveChatId.current !== undefined;

    if (isTablePanelOpen && chatIdChanged && hadPreviousChat) {
      resetTableState();
    }

    previousActiveChatId.current = activeChatId;
  }, [activeChatId, isTablePanelOpen, resetTableState]);

  // Close table panel when component unmounts
  useEffect(() => {
    return () => {
      if (isTablePanelOpen) {
        resetTableState();
      }
    };
  }, [isTablePanelOpen, resetTableState]);

  const openTablePanel = useCallback((tableId: string) => {
    setCurrentTableId(tableId);
    setIsTablePanelOpen(true);
    setError(null);
  }, []);

  const closeTablePanel = useCallback(() => {
    resetTableState();
  }, [resetTableState]);

  const setTableData = useCallback((tableId: string, data: AiTableArtifact) => {
    setTableArtifacts((prev) => ({
      ...prev,
      [tableId]: data,
    }));
  }, []);

  const value: TableArtifactContextType = {
    isTablePanelOpen,
    currentTableId,
    isLoading,
    error,
    tableArtifacts,
    openTablePanel,
    closeTablePanel,
    setTableData,
    setLoading: setIsLoading,
    setError,
  };

  return <TableArtifactContext.Provider value={value}>{children}</TableArtifactContext.Provider>;
};

export const useTableArtifacts = (): TableArtifactContextType => {
  const context = useContext(TableArtifactContext);
  if (!context) {
    throw new Error('useTableArtifacts must be used within a TableArtifactProvider');
  }
  return context;
};

/**
 * Optional version of useTableArtifacts that returns undefined if not within a provider.
 */
export const useTableArtifactsOptional = (): TableArtifactContextType | undefined => {
  const context = useContext(TableArtifactContext);
  return context ?? undefined;
};

